import type { Role } from './auth';

/**
 * The pre-defined development identities — the ONLY declaration of them.
 *
 * SIMSM stores no user accounts (caiet UC10); a person is whoever MPass says
 * they are. Until MPass is wired, `/dev-login` is the only way into the system,
 * and the e2e suite authenticates through the same endpoint. Both used to keep
 * their own list. Every entry disagreed, and two disagreed about the ROLE:
 * `2000000000003` was a Registrar Administrator on the page and a System
 * Administrator in the fixtures; `2000000000004` was the reverse. The fixture
 * file also gave `2000000000002` to two different people. A test asserting on an
 * IDNP and a human clicking that IDNP were getting different people.
 *
 * So there is one registry, here, and both read it. It lives in
 * `@simsm/shared-types` because that is the only project both `frontoffice` and
 * `frontoffice-e2e` may depend on: they are both tagged `type:app`, and
 * `@nx/enforce-module-boundaries` refuses an import between two of those.
 *
 * A persona carries what an MPass assertion carries, in MPass's own vocabulary
 * (docs/shape-ops-dev-identities.md §1): `mpassCodes` is the role-named
 * attribute, `REGISTRAR_ADMIN` → Registrar codes `{IDNO}0000`,
 * `REGISTRAR_OPERATOR` → location codes `{IDNO}NNNN` (caiet §Roluri, CF-08).
 * `devLoginBodyFor` turns that into the session request the same way
 * `resolveSamlProfile` does, so a dev operator is narrowed to its locations and
 * its Registrar is derived from their prefix — not handed over pre-digested.
 *
 * Every code names a Registrar and a site `tools/seed/dev-registrars.mjs`
 * actually creates (sites are `{IDNO}` + a 4-digit ordinal, in seed order). A
 * code the catalog has never heard of, or holds inactive, is dropped at login
 * with a warning, and a persona left with no Registrar or location is refused
 * (caiet UC10-06, docs/shape-ops-login-scopes.md §4).
 */
export const DEV_IDENTITY_KEYS = [
  'operator-single-location',
  'operator-two-locations',
  'operator-imsp',
  'operator-non-imsp',
  'operator-multi-role',
  'registrar-admin',
  'registrar-admin-two-registrars',
  'registrar-admin-imsp',
  'system-admin',
  'system-admin-observer',
  'observer-general',
  'observer-ms',
  'observer-cnam',
  'observer-ansp',
  'no-role',
] as const;

export type DevIdentityKey = (typeof DEV_IDENTITY_KEYS)[number];

export interface DevIdentity {
  key: DevIdentityKey;
  idnp: string;
  firstName: string;
  lastName: string;
  activeRole: Role;
  availableRoles: readonly Role[];
  /**
   * The role-named MPass attribute values, verbatim. Only the two Registrar
   * roles carry one; every other role is `global` in the caiet's §Roluri table.
   */
  mpassCodes?: Readonly<Partial<Record<MpassScopedRole, readonly string[]>>>;
}

/** The roles whose MPass attribute carries organisation codes. */
export type MpassScopedRole = 'REGISTRAR_ADMIN' | 'REGISTRAR_OPERATOR';

export const DEV_IDENTITIES: Readonly<Record<DevIdentityKey, DevIdentity>> = {
  'operator-single-location': {
    key: 'operator-single-location',
    idnp: '2000000000001',
    firstName: 'Maria',
    lastName: 'Popescu',
    activeRole: 'REGISTRAR_OPERATOR',
    availableRoles: ['REGISTRAR_OPERATOR'],
    mpassCodes: { REGISTRAR_OPERATOR: ['10066000000010001'] },
  },
  // DS-01 U-RO1ab's shape on the dev seed: one Registrar, two of its sites
  // (Depozit central …0001, Filiala Centru …0002). The only operator with more
  // than one location, which is what tells "narrowed to my locations" apart
  // from "narrowed to the first". The dev seed gives that Registrar exactly
  // these two sites, so by data alone it is not told apart from "every site";
  // its session is, because it lists them where an administrator's lists none.
  'operator-two-locations': {
    key: 'operator-two-locations',
    idnp: '2000000000023',
    firstName: 'Sorin',
    lastName: 'Lungu',
    activeRole: 'REGISTRAR_OPERATOR',
    availableRoles: ['REGISTRAR_OPERATOR'],
    mpassCodes: { REGISTRAR_OPERATOR: ['10066000000010001', '10066000000010002'] },
  },
  // caiet UC10-02 + arbitration D1: the Registrar kind decides who sees
  // Stocuri IMSP, so the IMSP identities and their non-IMSP counterpart point at
  // IDNOs that really carry those kinds in catalog.registrar. An IDNO absent
  // from the catalog would hide the entry for the wrong reason and prove nothing.
  'operator-imsp': {
    key: 'operator-imsp',
    idnp: '2000000000002',
    firstName: 'Ion',
    lastName: 'Munteanu',
    activeRole: 'REGISTRAR_OPERATOR',
    availableRoles: ['REGISTRAR_OPERATOR'],
    mpassCodes: { REGISTRAR_OPERATOR: ['10066000000030001'] },
  },
  'operator-non-imsp': {
    key: 'operator-non-imsp',
    idnp: '2000000000010',
    firstName: 'Nadejda',
    lastName: 'Rotaru',
    activeRole: 'REGISTRAR_OPERATOR',
    availableRoles: ['REGISTRAR_OPERATOR'],
    mpassCodes: { REGISTRAR_OPERATOR: ['10036001234560001'] },
  },
  // UC12's role switch is hidden for a single-role session, which reads as "the
  // switch is broken" unless one identity carries two roles.
  'operator-multi-role': {
    key: 'operator-multi-role',
    idnp: '2000000000005',
    firstName: 'Vlad',
    lastName: 'Cernat',
    activeRole: 'REGISTRAR_OPERATOR',
    availableRoles: ['REGISTRAR_OPERATOR', 'REGISTRAR_ADMIN'],
    mpassCodes: {
      REGISTRAR_OPERATOR: ['10066000000010001'],
      REGISTRAR_ADMIN: ['10066000000010000'],
    },
  },
  'registrar-admin': {
    key: 'registrar-admin',
    idnp: '2000000000003',
    firstName: 'Ana',
    lastName: 'Vasilescu',
    activeRole: 'REGISTRAR_ADMIN',
    availableRoles: ['REGISTRAR_ADMIN'],
    mpassCodes: { REGISTRAR_ADMIN: ['10066000000010000'] },
  },
  // The one identity whose scope spans two Registrars, which is how the
  // absence of a Registrar selector is reproducible by hand (report D-14).
  'registrar-admin-two-registrars': {
    key: 'registrar-admin-two-registrars',
    idnp: '2000000000011',
    firstName: 'Maria',
    lastName: 'Ionescu',
    activeRole: 'REGISTRAR_ADMIN',
    availableRoles: ['REGISTRAR_ADMIN'],
    mpassCodes: { REGISTRAR_ADMIN: ['10066000000010000', '10066000000020000'] },
  },
  'registrar-admin-imsp': {
    key: 'registrar-admin-imsp',
    idnp: '2000000000009',
    firstName: 'Doina',
    lastName: 'Rusu',
    activeRole: 'REGISTRAR_ADMIN',
    availableRoles: ['REGISTRAR_ADMIN'],
    mpassCodes: { REGISTRAR_ADMIN: ['10046002345670000'] },
  },
  'system-admin': {
    key: 'system-admin',
    idnp: '2000000000004',
    firstName: 'Petru',
    lastName: 'Bivol',
    activeRole: 'SYSTEM_ADMIN',
    availableRoles: ['SYSTEM_ADMIN'],
  },
  'system-admin-observer': {
    key: 'system-admin-observer',
    idnp: '2000000000008',
    firstName: 'Petru',
    lastName: 'Bivol',
    activeRole: 'SYSTEM_ADMIN',
    availableRoles: ['SYSTEM_ADMIN', 'OBSERVER'],
  },
  // DS-01 U-OBS, same IDNP: the general Observer, national and unrestricted
  // (analytics.observer_role_definition `OBSERVER`). TC-UC10-09-01 compares
  // against it.
  'observer-general': {
    key: 'observer-general',
    idnp: '2000000000015',
    firstName: 'Lilia',
    lastName: 'Ciobanu',
    activeRole: 'OBSERVER',
    availableRoles: ['OBSERVER'],
  },
  'observer-ms': {
    key: 'observer-ms',
    idnp: '2000000000006',
    firstName: 'Vasile',
    lastName: 'Stoica',
    activeRole: 'OBSERVER_MS',
    availableRoles: ['OBSERVER_MS'],
  },
  'observer-cnam': {
    key: 'observer-cnam',
    idnp: '2000000000007',
    firstName: 'Elena',
    lastName: 'Crăciun',
    activeRole: 'OBSERVER_CNAM',
    availableRoles: ['OBSERVER_CNAM'],
  },
  // The one seeded observer whose boundary includes a Registrar kind (IMSP)
  // as well as a medicine tag, so the kind half of role-boundary.ts is
  // reachable by hand. No DS-01 user carries it; the IDNP is one DS-01 leaves free.
  'observer-ansp': {
    key: 'observer-ansp',
    idnp: '2000000000024',
    firstName: 'Andrei',
    lastName: 'Moraru',
    activeRole: 'OBSERVER_ANSP',
    availableRoles: ['OBSERVER_ANSP'],
  },
  // caiet UC10-06 — the only identity that is MEANT to be refused.
  // OBSERVER_UNDEFINED is shaped like an observer code but is not in
  // analytics.observer_role_definition, which is exactly the state an MPass
  // assertion is in when it carries a role SIMSM does not honour.
  'no-role': {
    key: 'no-role',
    idnp: '2000000000099',
    firstName: 'Cont',
    lastName: 'FărăRol',
    activeRole: 'OBSERVER_UNDEFINED',
    availableRoles: ['OBSERVER_UNDEFINED'],
  },
};

/** The registry in the order `/dev-login` offers it. */
export const DEV_IDENTITY_LIST: readonly DevIdentity[] = DEV_IDENTITY_KEYS.map(
  (key) => DEV_IDENTITIES[key],
);

/** The body `POST /auth/dev-login` takes (`apps/bff/src/auth/dtos/dev-login.dto.ts`). */
export interface DevLoginBody {
  idnp: string;
  firstName: string;
  lastName: string;
  activeRole: Role;
  availableRoles: Role[];
  /** Bare 13-digit IDNOs. */
  registrarScope: string[];
  /** MPass location codes; the bff resolves them to `catalog.site` ids. */
  locationScope: string[];
}

// `{IDNO}{NNNN}`: 13 digits of the legal entity, 4 of the unit. The pattern
// `role-mapping.ts` applies: a bare IDNO passes, anything else is dropped.
const MPASS_ORG_CODE = /^(\d{13})(?:\d{4})?$/;

/** The Registrar IDNO an MPass organisation code belongs to (CF-08), or `''`. */
export function registrarIdnoOf(code: string): string {
  return MPASS_ORG_CODE.exec(code)?.[1] ?? '';
}

/**
 * The dev-login request that yields the session real SAML would for this
 * persona: `resolveSamlProfile`'s rule, applied to `mpassCodes`.
 * `locationScope` is the operator attribute verbatim; `registrarScope` is the
 * Registrar behind every admin code and every location code, deduplicated.
 * An attribute counts only for a role the persona actually holds.
 *
 * The client does the prefix step because `createSessionForDev` takes
 * `registrarScope` as IDNOs and looks each one up verbatim.
 */
export function devLoginBodyFor(identity: DevIdentity): DevLoginBody {
  const codesFor = (role: MpassScopedRole): readonly string[] =>
    identity.availableRoles.includes(role) ? (identity.mpassCodes?.[role] ?? []) : [];
  const locationScope = [...codesFor('REGISTRAR_OPERATOR')];
  const registrarScope = [
    ...new Set(
      [...codesFor('REGISTRAR_ADMIN'), ...locationScope]
        .map(registrarIdnoOf)
        .filter((idno) => idno.length > 0),
    ),
  ];
  return {
    idnp: identity.idnp,
    firstName: identity.firstName,
    lastName: identity.lastName,
    activeRole: identity.activeRole,
    availableRoles: [...identity.availableRoles],
    registrarScope,
    locationScope,
  };
}
